import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { getItemWithExpiration } from '../lib/utils';
import {
    createTempMember,
    CreateTempMemberParamsType,
    deleteTempMember,
    DeleteTempMemberParamsType,
} from '../api/member';
import { MemberProfile } from './useMemberQueryService';
import { invalidateSuperAdminDashboardQueries } from '../lib/superAdminCache';

type Props = {
    onCreateTempMemberSuccess?: (data: MemberProfile) => void;
    onCreateTempMemberError?: (result: { status: number; data?: any }) => void;
    onDeleteTempMemberSuccess?: () => void;
    /** 삭제가 204 가 아닌 채로 끝났을 때. 공유 풀 소유자 차단(409)이 여기로 온다 */
    onDeleteTempMemberError?: (result: { status?: number; data?: any } | null | undefined) => void;
};

const useMemberMutationService = ({
    onCreateTempMemberSuccess,
    onCreateTempMemberError,
    onDeleteTempMemberSuccess,
    onDeleteTempMemberError,
}: Props) => {
    const [authorization] = useState(getItemWithExpiration('authorization'));
    const queryClient = useQueryClient();

    const invalidateMemberDomainQueries = async () => {
        await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['getCenterMemberList'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterMemberListQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getCenterMemberListAPIQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getAllMembershipForMemberQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getMembershipForMemberQuery'] }),
            queryClient.invalidateQueries({ queryKey: ['getMemberListForCenterAPI'] }),
            queryClient.invalidateQueries({ queryKey: ['getTrainerMemberCountAPI'] }),
            queryClient.invalidateQueries({ queryKey: ['groupTicket'] }),
            queryClient.invalidateQueries({ queryKey: ['publicMember'] }),
            invalidateSuperAdminDashboardQueries(queryClient),
        ]);
    };

    // 임시회원 생성
    const createTempMemberMutation = useMutation(
        ['createTempMemberMutation'],
        async (params: CreateTempMemberParamsType) => createTempMember({ ...params, accessToken: authorization }),
        {
            onError: (error) => {
                console.log(error);
            },
            onSuccess: async ({ status, data }) => {
                if (status === 201) {
                    const member: MemberProfile = data.member;
                    const isConnectedExisting = data.connected === true;
                    if (isConnectedExisting) {
                        alert('기존 회원과 강사가 연결되었습니다.');
                    }
                    // 회원은 등록됐지만 이용권 일부가 만들어지지 않은 경우.
                    // 서버가 이 목록을 줄 때만 뜬다(구버전 서버는 필드 자체가 없다).
                    // 조용히 넘기면 이용권이 사라진 걸 아무도 모른다.
                    const facilitySubscriptionFailures = data.facilitySubscriptionFailures;
                    if (Array.isArray(facilitySubscriptionFailures) && facilitySubscriptionFailures.length > 0) {
                        // outcome 'unknown' 은 저장됐는지 서버도 모르는 경우다.
                        // 확인 없이 다시 발급하면 이용권이 두 건이 되므로 문구를 갈라 안내한다.
                        const hasUnknown = facilitySubscriptionFailures.some((failure: any) => failure?.outcome === 'unknown');
                        alert(
                            '회원은 등록됐지만 아래 이용권은 등록되지 않았어요.\n' +
                                (hasUnknown
                                    ? '회원 상세 화면에서 등록 여부를 먼저 확인한 뒤 발급해주세요.\n\n'
                                    : '회원 상세 화면에서 다시 발급해주세요.\n\n') +
                                facilitySubscriptionFailures
                                    .map((failure: any) => `· ${failure?.productName ?? '이용권'}: ${failure?.message ?? '등록 실패'}`)
                                    .join('\n')
                        );
                    }
                    onCreateTempMemberSuccess && onCreateTempMemberSuccess(member);
                    void invalidateMemberDomainQueries();
                } else if (status === 413) {
                    alert('이미 존재하는 회원입니다.');
                } else if (status >= 400) {
                    onCreateTempMemberError && onCreateTempMemberError({ status, data });
                }
            },
        }
    );
    // 임시회원 삭제
    const deleteTempMemberMutation = useMutation(
        ['deleteTempMemberMutation'],
        async (params: DeleteTempMemberParamsType) => deleteTempMember({ ...params }),
        {
            onError: (error) => {
                console.log(error);
                onDeleteTempMemberError && onDeleteTempMemberError(null);
            },
            // result 를 구조분해하지 않는다 — 실패 응답이 비어 오면 콜백 자체가 터져
            //   성공도 실패도 화면에 안 남는 무음 실패가 된다.
            onSuccess: async (result) => {
                if (result?.status === 204) {
                    onDeleteTempMemberSuccess && onDeleteTempMemberSuccess();
                    void invalidateMemberDomainQueries();
                    return;
                }
                onDeleteTempMemberError && onDeleteTempMemberError(result);
            },
        }
    );

    return { createTempMemberMutation, deleteTempMemberMutation };
};

export default useMemberMutationService;
